import { useMemo } from "react";

import type { Variables } from "../graphql";
import { DEFAULT_GROUP, useGraphqlObjConfig } from "../graphql";
import type { GqlObj } from "../graphql/types";
import { useMountQueryKey } from "../MountQueryKeyContext";
import type { QueryKey } from "./types";

const EMPTY_GROUP: never[] = [];

export interface UseQueryKeyOptions<TVars extends Variables | undefined> {
  variables?: TVars;
  query: GqlObj;
  updateGroups?: string[];
}

export const useQueryKey = <TVars extends Variables | undefined = undefined>({
  query,
  variables,
  updateGroups,
}: UseQueryKeyOptions<TVars>): QueryKey<TVars> => {
  const { endpoint, namespace } = useGraphqlObjConfig(query);
  const { onMountQueryKey } = useMountQueryKey();

  const groups = useMemo(
    () => [
      ...new Set([
        ...(query.directives.groups || EMPTY_GROUP),
        ...(updateGroups || EMPTY_GROUP),
      ]),
    ],
    [query.directives.groups, updateGroups],
  );

  const memoizedQuery = useMemo(
    () => ({
      ...query,
      directives: {
        ...query.directives,
        groups: groups.length > 0 ? groups : [DEFAULT_GROUP],
      },
    }),
    [query, groups],
  );

  const queryKey: QueryKey<TVars> = [
    query.operationName,
    {
      query: memoizedQuery,
      variables,
      graphQLEndpoint: endpoint,
      namespace,
    },
  ];

  onMountQueryKey(queryKey);

  return queryKey;
};
